import React, { useState, useEffect } from 'react';
import ApiClient from "../utils/ApiClient";

export default function PoList() {
    // State declarations
    const [poData, setPoData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});

    // Toggle row expansion
    const toggleExpanded = (poId) => {
        setExpandedRows(prev => {
            const newState = {
                ...prev,
                [poId]: !prev[poId]
            };
            return newState;
        });
    };

    // Check if a row is expanded
    const isExpanded = (poId) => {
        return !!expandedRows[poId];
    };

    // Get cell value for a PO row
    const getPoValue = (po, columnId) => {
        switch (columnId) {
            case 'po_number':
                return po.poNumber || po.po_number;
            default:
                return po[columnId];
        }
    };

    // Format cell value
    const formatValue = (value, type) => {
        if (value == null) return '-';

        switch (type) {
            case 'number':
                return value.toLocaleString();
            case 'date':
                return new Date(value).toLocaleDateString();
            case 'percentage':
                return `${value}%`;
            default:
                return value;
        }
    };

    // Format percentage with color
    const formatPercentage = (percentage) => {
        if (percentage == null) return <span>-</span>;

        let bgColor, textColor;
        if (percentage === 100) {
            bgColor = 'bg-green-100';
            textColor = 'text-green-800';
        } else if (percentage >= 50) {
            bgColor = 'bg-yellow-100';
            textColor = 'text-yellow-800';
        } else {
            bgColor = 'bg-red-100';
            textColor = 'text-red-800';
        }

        return (
            <span className={`px-2 py-1 rounded-full ${bgColor} ${textColor}`}>
                {percentage}%
            </span>
        );
    };

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await ApiClient.post('/api/pos/polist');
                if (response.data && Array.isArray(response.data)) {
                    setPoData(response.data);
                } else {
                    throw new Error('Invalid response structure');
                }
            } catch (error) {
                console.error('Error fetching PO data:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Define column headers
    const columns = [
        { id: 'po_number', label: 'PO Number', align: 'left' },
        { id: 'unique_skus', label: 'Unique SKUs', align: 'left' },
        { id: 'packing_list_count', label: 'Packing Lists', align: 'left' },
        { id: 'total_ordered', label: 'Ordered', align: 'right', format: 'number' },
        { id: 'total_received', label: 'Received', align: 'right', format: 'number' },
        { id: 'total_backordered', label: 'Backordered', align: 'right', format: 'number' },
        { id: 'total_damaged', label: 'Damaged', align: 'right', format: 'number' },
        { id: 'total_canceled', label: 'Canceled', align: 'right', format: 'number' },
        { id: 'received_percentage', label: 'Received %', align: 'right', format: 'percentage' },
        { id: 'first_entry_date', label: 'First Entry', align: 'left', format: 'date' },
        { id: 'last_entry_date', label: 'Last Entry', align: 'left', format: 'date' },
        { id: 'days_difference', label: 'Days Diff', align: 'right' }
    ];

    // Conditional rendering for loading and error states
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-gray-600">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-red-600">Error loading data: {error}</div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold text-gray-900">Purchase Orders/ Packing Lists</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        A comprehensive list of all POs associated with their respective Packing Lists
                    </p>
                </div>
            </div>
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.id}
                                        className={`py-3.5 text-sm font-semibold text-gray-900 ${
                                            column.align === 'right' ? 'text-right' : 'text-left'
                                        } px-3`}
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {poData.map((po, poIndex) => {
                                // Create a unique identifier for each PO row
                                const poId = `po-${poIndex}`;
                                const expanded = isExpanded(poId);
                                const hasPackingLists = po.packingLists && po.packingLists.length > 0;

                                // Prepare unique objects for keys
                                const uniquePoKey = `po-${poIndex}-${getPoValue(po, 'po_number') || ''}-${Date.now()}`;

                                return (
                                    <React.Fragment key={uniquePoKey}>
                                        {/* Main PO row */}
                                        <tr
                                            className="hover:bg-gray-100 cursor-pointer"
                                            onClick={() => toggleExpanded(poId)}
                                        >
                                            {columns.map((column) => {
                                                if (column.id === 'po_number') {
                                                    return (
                                                        <td key={`${uniquePoKey}-${column.id}`} className="py-4 text-sm whitespace-nowrap text-left pl-3">
                                                            <div className="flex items-center">
                                                                    <span
                                                                        className="mr-2 cursor-pointer"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleExpanded(poId);
                                                                        }}
                                                                    >
                                                                        {expanded ? '▼' : '▶'}
                                                                    </span>
                                                                <span className="text-gray-900 font-medium">
                                                                        {getPoValue(po, 'po_number') || '-'}
                                                                    </span>
                                                            </div>
                                                        </td>
                                                    );
                                                } else if (column.id === 'received_percentage') {
                                                    return (
                                                        <td key={`${uniquePoKey}-${column.id}`} className="py-4 text-sm whitespace-nowrap text-right pr-3">
                                                            {formatPercentage(getPoValue(po, column.id))}
                                                        </td>
                                                    );
                                                } else {
                                                    return (
                                                        <td
                                                            key={`${uniquePoKey}-${column.id}`}
                                                            className={`py-4 text-sm whitespace-nowrap ${
                                                                column.align === 'right' ? 'text-right pr-3' : 'text-left pl-3'
                                                            }`}
                                                        >
                                                                <span className="text-gray-500">
                                                                    {formatValue(getPoValue(po, column.id), column.format)}
                                                                </span>
                                                        </td>
                                                    );
                                                }
                                            })}
                                        </tr>

                                        {/* Render packing lists if expanded */}
                                        {expanded && (
                                            <>
                                                {hasPackingLists ? (
                                                    po.packingLists.map((packingList, packingListIndex) => {
                                                        const uniquePlKey = `${uniquePoKey}-pl-${packingListIndex}-${packingList.calibrate_receive_inventory_packing_list_id || ''}-${Date.now()}`;

                                                        return (
                                                            <tr key={uniquePlKey} className="bg-gray-50">
                                                                {columns.map((column) => {
                                                                    // Special handling for PO number column
                                                                    if (column.id === 'po_number') {
                                                                        return (
                                                                            <td key={`${uniquePlKey}-${column.id}`} className="py-4 text-sm whitespace-nowrap text-left pl-8">
                                                                                    <span className="text-gray-500">
                                                                                        {packingList.packing_list_name ||
                                                                                            `Packing List`}
                                                                                    </span>
                                                                            </td>
                                                                        );
                                                                    }

                                                                    // Map field names between PO and packing list
                                                                    let value;
                                                                    switch (column.id) {
                                                                        case 'po_number':
                                                                            value = packingList.calibrate_receive_inventory_packing_list_id || packingList.packing_list_id;
                                                                            break;
                                                                        case 'unique_skus':
                                                                            value = packingList.sku_count;
                                                                            break;
                                                                        case 'total_received':
                                                                            value = packingList.received_qty;
                                                                            break;
                                                                        case 'total_backordered':
                                                                            value = packingList.backordered_qty;
                                                                            break;
                                                                        case 'total_damaged':
                                                                            value = packingList.damaged_qty;
                                                                            break;
                                                                        case 'total_canceled':
                                                                            value = packingList.canceled_qty;
                                                                            break;
                                                                        default:
                                                                            value = packingList[column.id];
                                                                    }

                                                                    // Special handling for percentage column
                                                                    if (column.id === 'received_percentage') {
                                                                        return (
                                                                            <td key={`${uniquePlKey}-${column.id}`} className="py-4 text-sm whitespace-nowrap text-right pr-3">
                                                                                {formatPercentage(value)}
                                                                            </td>
                                                                        );
                                                                    }

                                                                    // Render regular cell
                                                                    return (
                                                                        <td
                                                                            key={`${uniquePlKey}-${column.id}`}
                                                                            className={`py-4 text-sm whitespace-nowrap ${
                                                                                column.align === 'right' ? 'text-right pr-3' : 'text-left pl-3'
                                                                            }`}
                                                                        >
                                                                                <span className="text-gray-500">
                                                                                    {formatValue(value, column.format)}
                                                                                </span>
                                                                        </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr key={`${uniquePoKey}-empty`} className="bg-gray-50">
                                                        <td colSpan={columns.length} className="py-4 text-sm text-center text-gray-500">
                                                            No packing lists available for this PO
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}